import * as React from "react";
import { useCallback } from "react";

import { Formik } from "formik";
import { toast } from "react-toastify";
import { useHistory, useParams } from "react-router-dom";

import {
  WarningBanner,
  PrimaryButton,
  SecondaryButton,
  Spinner,
} from "@graffer-inc/console-design-kit";

import apiClient from "../../../../../application/apis/tenant-management/tenant-management-client";
import { CloudGovApiError } from "../../../../../application/domain/api-error";
import { reportUnexpectedError } from "../../../../../error-reporter";
import { RBACHidden } from "../../../../helpers/rbac/rbac";
import { ServiceProviderTemplate } from "../../../../template/service-provider/default-template";
import { ServiceProviderR } from "../../routes";
import {
  UserCreate,
  UserCreateValues,
} from "../../../../organisms/settings/user-create";
import { Roles } from "../../../../../graphql/generated/types";
import { UserAuthMethod } from "../../../../../application/domain/user/user";
import {
  CreateUser,
  CreateUserYup,
} from "../../../../../application/domain/user/user-form-validation";

export const UsersNewPage: React.FC = () => {
  const history = useHistory();
  const { orgId } = useParams<{ orgId: string }>();

  const [isLoading, setIsLoading] = React.useState(false);
  const onCreateUser = useCallback(
    async (createUser: CreateUser): Promise<boolean> => {
      setIsLoading(true);
      try {
        await apiClient.createUserByServiceProvider(orgId, createUser);
        toast.success(`${createUser.accountName}を登録しました。`);
        history.push(ServiceProviderR.OrgUsers.link({ orgId }));
        return true;
      } catch (e) {
        if (CloudGovApiError.orgUserExists(e)) {
          toast.error(`${createUser.accountName}は既に登録されています。`);
          return false;
        }
        if (
          CloudGovApiError.isErrorCode(
            e,
            "org_user_with_same_email_already_exists",
          )
        ) {
          const err = new CloudGovApiError(e);
          const errResponse = err.response as any as {
            msg: string;
          };
          toast.error(
            `${createUser.emails.join("")}は、すでにアカウント${errResponse.msg}に登録されているため、仮パスワード送信ができません。`,
          );
          return false;
        }

        reportUnexpectedError(e);
        toast.error(`${createUser.accountName}の登録に失敗しました。`);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [history, orgId],
  );
  return (
    <ServiceProviderTemplate
      breadcrumbs={[
        {
          to: ServiceProviderR.Home.link({}),
          title: "Home",
        },
        {
          to: ServiceProviderR.OrgUsers.link({ orgId }),
          title: "テナントユーザー",
        },
        {
          to: ServiceProviderR.OrgUsersNew.link({ orgId }),
          title: "新規テナントユーザー登録",
        },
      ]}
    >
      <RBACHidden
        requirePermissions="users.create"
        hiddenChildren={
          <WarningBanner message="テナントユーザーを作成する権限がありません。" />
        }
      >
        <Spinner loading={isLoading} />
        <h1 className="title">新規テナントユーザー登録</h1>
        <div
          style={{
            maxWidth: "1000px",
          }}
        >
          <Formik
            initialValues={
              {
                name: "",
                accountName: "",
                password: "",
                doSendInvitation: false,
                roles: ["Staff"] as Array<Roles>,
                groupsRoles: {},
                emails: [] as Array<string>,
                authMethod: "password" as UserAuthMethod,
                externalUserId: "",
              } as UserCreateValues
            }
            validationSchema={CreateUserYup}
            onSubmit={async (values, { resetForm }) => {
              // 一度フォーカスが当たると空文字が追加されるため、空文字を削除
              values.emails = values.emails.filter((email) => email !== "");
              const created = await onCreateUser(values);
              if (created) {
                resetForm();
              }
            }}
          >
            {({ handleSubmit, values }) => (
              <form onSubmit={handleSubmit}>
                <div className="buttons is-pulled-right">
                  <SecondaryButton
                    text="キャンセル"
                    onClick={() => {
                      history.push(ServiceProviderR.OrgUsers.link({ orgId }));
                    }}
                  />
                  <PrimaryButton
                    text="登録"
                    onClick={() => {
                      onCreateUser;
                    }}
                  />
                </div>
                <br />
                <UserCreate
                  values={values}
                  isServiceProviderAccount
                  orgId={orgId}
                />
              </form>
            )}
          </Formik>
        </div>
      </RBACHidden>
    </ServiceProviderTemplate>
  );
};
